import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import {
  CitationList,
  ContentButton,
  ContentDiv,
  PersonaCard,
} from "../elements/card-group";
import { useFunctionalGraphPagination } from "@/hooks/use-functional-graph-pagination";
import { PersonaIcon } from "@/assets/svg-icons/persona-icon";
import { Pagination } from "../elements/pagination";
import { NodeCitation } from "@/service-hooks/use-get-functional-graph-service";
import { useGetNodeCitations } from "@/service-hooks/use-get-node-citations-service";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFunctionalTreeData } from "@/hooks/use-functional-tree-data";
import { Button } from "../ui/button";
import { FunctionalNodeDialog } from "../elements/functional-node-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui/dialog";
import ConfirmDialog from "../elements/confirm-dialog";
import MergeConfirmationDialog from "../elements/merge-confirmation-dialog";
import {
  useCreateFunctionalNode,
  useMergeFunctionalNodes,
} from "@/service-hooks/use-create-functional-node-service";
import { useUpdateFunctionalNode } from "@/service-hooks/use-update-functional-node-service";
import { useDeleteFunctionalNode } from "@/service-hooks/use-delete-functional-node-service";
import { useClonePersona } from "@/service-hooks/use-clone-persona-service";
import { useCloneScenario } from "@/service-hooks/use-clone-scenario-service";
import { useCloneStep } from "@/service-hooks/use-clone-step-service";
import { useCloneAction } from "@/service-hooks/use-clone-action-service";
import { useCloneApi } from "@/service-hooks/use-clone-api-service";
import { useGenerateEmbeddings } from "@/service-hooks/use-generate-embeddings-service";
import { useUser } from "@/hooks/use-user";
import { Plus, Pencil, Trash2, Merge, Copy, RotateCw, Sparkles, Loader2, Eye, Filter, Download, Palette } from "lucide-react";
import CustomTooltip from "../elements/custom-tooltip";
import { toast } from "sonner";
import { getDocumentsSignedUrlById } from "@/service-hooks/use-get-document-service";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import FunctionalGraphFilterPanel, {
  FilterRow,
  FilterableLabel,
} from "../elements/functional-graph-filter-panel";
import FunctionalGraphFilterChips from "../elements/functional-graph-filter-chips";

// BREEZEAI-1054: system + label-canned fields to omit when harvesting real
// custom-property keys off a fetched node. Everything else (including case
// like `mode` vs `First`) surfaces as-is in the filter panel's autocomplete,
// so users pick the exact case that's stored in Neo4j.
const OMIT_SYSTEM_KEYS: ReadonlySet<string> = new Set([
  "id",
  "functionalId",
  "projectUuid",
  "createdAt",
  "updatedAt",
  "embedding",
  "embeddingStatus",
  "embeddingClaimedAt",
  "embeddedAt",
  "embeddingPlatform",
  "embedTextVersion",
  "citations",
  "personaId",
  "outcomeId",
  "scenarioId",
  "stepId",
  "actionId",
  "preSteps",
  "postSteps",
  "screenshot",
]);
const LABEL_CANNED_KEYS: Record<FilterableLabel, ReadonlySet<string>> = {
  Persona: new Set(["persona", "description", "nameKey", "embedText", "citationRefs"]),
  Outcome: new Set(["outcome", "description", "nameKey", "embedText", "citationRefs"]),
  Scenario: new Set(["scenario", "description", "nameKey", "embedText", "citationRefs"]),
  Step: new Set(["step", "description", "order", "nameKey", "embedText", "citationRefs"]),
  Action: new Set(["action", "description", "order", "nameKey", "embedText", "citationRefs"]),
  Api: new Set(["url", "method", "type", "description", "request", "response"]),
};
const extractCustomKeys = (
  items: unknown[] | undefined,
  label: FilterableLabel,
): string[] => {
  if (!items || items.length === 0) return [];
  const canned = LABEL_CANNED_KEYS[label];
  const seen = new Set<string>();
  for (const item of items) {
    if (item && typeof item === "object") {
      for (const k of Object.keys(item as Record<string, unknown>)) {
        if (!OMIT_SYSTEM_KEYS.has(k) && !canned.has(k)) seen.add(k);
      }
    }
  }
  return Array.from(seen).sort();
};
// import { config } from "@/config";
// import { apiFetch } from "@/lib/api-client";
// NOTE: Import removed — backend now cascade-deletes all descendants
// import { useFunctionalChildCounts } from "@/hooks/use-functional-child-counts";
import { ClonePersonaDialog } from "../elements/clone-persona-dialog";
import { CloneScenarioDialog } from "../elements/clone-scenario-dialog";
import { CloneStepDialog } from "../elements/clone-step-dialog";
import { CloneActionDialog } from "../elements/clone-action-dialog";
import { CloneApiDialog } from "../elements/clone-api-dialog";
import { useProjectRole } from "@/context/project-role-context";

interface FunctionalTabLayoutProps {
  onImportCsvClick?: () => void;
  actionsPortalRef?: React.RefObject<HTMLDivElement | null>;
}

export default function FunctionalTabLayout({
  actionsPortalRef,
}: FunctionalTabLayoutProps = {}) {
  // BREEZEAI-1054: attribute-based filter state lives here — this tab is the
  // only functional surface that hosts the filter UI (the graph view has none
  // by design). `appliedFilters` is fed to `useFunctionalGraphPagination`
  // below, which merges label-scoped rows into each per-label query.
  const [appliedFilters, setAppliedFilters] = useState<FilterRow[]>([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const {
    // Data
    personas,
    outcomes,
    scenarios,
    steps,
    actions,
    apis,

    // Total counts
    personasTotal,
    outcomesTotal,
    scenariosTotal,
    stepsTotal,
    actionsTotal,
    apisTotal,

    // Page info
    personaPage,
    outcomePage,
    scenarioPage,
    stepPage,
    actionPage,
    apiPage,
    personasTotalPages,
    outcomesTotalPages,
    scenariosTotalPages,
    stepsTotalPages,
    actionsTotalPages,
    apisTotalPages,

    // Selected items
    selectedPersona,
    selectedOutcome,
    selectedScenario,
    selectedStep,
    selectedAction,

    // Handlers
    handlePersonaClick,
    handleOutcomeClick,
    handleScenarioClick,
    handleStepClick,
    handleActionClick,

    // Page navigation handlers
    handlePersonaNextPage,
    handlePersonaPrevPage,
    handleOutcomeNextPage,
    handleOutcomePrevPage,
    handleScenarioNextPage,
    handleScenarioPrevPage,
    handleStepNextPage,
    handleStepPrevPage,
    handleActionNextPage,
    handleActionPrevPage,
    handleApiNextPage,
    handleApiPrevPage,

    // Query objects for reference
    personaQuery,
    outcomeQuery,
    scenarioQuery,
    stepQuery,
    actionQuery,
    apiQuery,

    // Loading states
    isLoadingOutcomes,
    isLoadingScenarios,
    isLoadingSteps,
    isLoadingActions,
    isLoadingApis,

    // Search states
    outcomeSearch,
    setOutcomeSearch,
    scenarioSearch,
    setScenarioSearch,
    stepSearch,
    setStepSearch,
    actionSearch,
    setActionSearch,
    apiSearch,
    setApiSearch,
    projectUuid,
    setSelectedPersona,
    setSelectedOutcome,
    setSelectedScenario,
    setSelectedStep,
    setSelectedAction,
  } = useFunctionalGraphPagination(appliedFilters);

  const handleApplyFilters = (rows: FilterRow[]) => {
    setAppliedFilters(rows);
    setIsFilterPanelOpen(false);
  };
  const handleRemoveFilter = (rowId: string) => {
    setAppliedFilters((prev) => prev.filter((r) => r.id !== rowId));
  };
  const handleClearAllFilters = () => setAppliedFilters([]);

  // Force re-render after mount so the portal ref is available
  const [, setPortalReady] = useState(false);
  useEffect(() => { setPortalReady(true); }, []);

  const queryClient = useQueryClient();
  const { user } = useUser();
  const isAdmin = user?.role === "ADMIN";
  const { isViewer } = useProjectRole();
  const [isExporting, setIsExporting] = useState(false);
  const { refetch: refetchTree } = useFunctionalTreeData(projectUuid, false);

  // Collapse all expanded levels when a snapshot is restored, so the user
  // doesn't see stale outcomes/scenarios/steps/actions. Fresh data will be
  // fetched on the next click.
  useEffect(() => {
    const handleSnapshotRestored = () => {
      setSelectedPersona(null);
      setSelectedOutcome(null);
      setSelectedScenario(null);
      setSelectedStep(null);
      setSelectedAction(null);
    };
    window.addEventListener("snapshot-restored", handleSnapshotRestored);
    return () => {
      window.removeEventListener("snapshot-restored", handleSnapshotRestored);
    };
  }, [
    setSelectedPersona,
    setSelectedOutcome,
    setSelectedScenario,
    setSelectedStep,
    setSelectedAction,
  ]);

  // NOTE: Hierarchy guard removed — backend now cascade-deletes all descendants
  // const personaOutcomeCounts = useFunctionalChildCounts(
  //   projectUuid,
  //   "Outcome",
  //   "personaId",
  //   (personas as any[])?.map((p) => p.id) ?? []
  // );
  // const outcomeScenarioCounts = useFunctionalChildCounts(
  //   projectUuid,
  //   "Scenario",
  //   "outcomeId",
  //   (outcomes as any[])?.map((o) => o.id) ?? []
  // );

  const { mutate: createNode } = useCreateFunctionalNode((data, err) => {
    if (err) {
      if(!!err.message){
        toast.error(err.message);
      }else{
        toast.error(
          `Failed to create ${data?.label || "item"}. Please try again.`
        );
      }
    } else {
      toast.success(`${data?.label || "Item"} created successfully`);
      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      if (data?.label === "Persona") personaQuery.refetch();
      if (data?.label === "Outcome") outcomeQuery.refetch();
      if (data?.label === "Scenario") scenarioQuery.refetch();
      if (data?.label === "Step") stepQuery.refetch();
      if (data?.label === "Action") actionQuery.refetch();
      if (data?.label === "Api") apiQuery.refetch();
    }
  });

  const { mutate: updateNode } = useUpdateFunctionalNode((data, err) => {
    if (err) {
      toast.error(
        `Failed to update ${data?.label || "item"}. Please try again.`
      );
    } else {
      toast.success(`${data?.label || "Item"} updated successfully`);
      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      if (data?.label === "Persona") personaQuery.refetch();
      if (data?.label === "Outcome") outcomeQuery.refetch();
      if (data?.label === "Scenario") scenarioQuery.refetch();
      if (data?.label === "Step") stepQuery.refetch();
      if (data?.label === "Action") actionQuery.refetch();
      if (data?.label === "Api") apiQuery.refetch();
    }
  });

  const { mutate: deleteNode } = useDeleteFunctionalNode({
    onSuccess: (_, variables) => {
      const { label, id } = variables;
      toast.success(`${label} deleted successfully`);

      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });

      // Use refetch for immediate UI update and handle state cleanup
      if (label === "Persona") {
        personaQuery.refetch();
        if (selectedPersona?.id === id) {
          setSelectedPersona(null);
          setSelectedOutcome(null);
          setSelectedScenario(null);
          setSelectedStep(null);
          setSelectedAction(null);
        }
      }
      if (label === "Outcome") {
        outcomeQuery.refetch();
        if (selectedOutcome?.id === id) {
          setSelectedOutcome(null);
          setSelectedScenario(null);
          setSelectedStep(null);
          setSelectedAction(null);
        }
      }
      if (label === "Scenario") {
        scenarioQuery.refetch();
        if (selectedScenario?.id === id) {
          setSelectedScenario(null);
          setSelectedStep(null);
          setSelectedAction(null);
        }
      }
      if (label === "Step") {
        stepQuery.refetch();
        if (selectedStep?.id === id) {
          setSelectedStep(null);
          setSelectedAction(null);
        }
      }
      if (label === "Action") {
        actionQuery.refetch();
        if (selectedAction?.id === id) {
          setSelectedAction(null);
        }
      }
      if (label === "Api") {
        apiQuery.refetch();
      }
    },
    onError: (err: any, variables) => {
      toast.error(
        err?.message ||
          `Failed to delete ${variables?.label || "item"}. Please try again.`
      );
    },
  });

  // Track node IDs currently being merged to show in-progress state
  const [mergingNodeIds, setMergingNodeIds] = useState<Set<string>>(new Set());

  const { mutate: mergeNodes } = useMergeFunctionalNodes(
    (data: any, err: Error | null) => {
      setMergingNodeIds(new Set());
      if (err) {
        toast.error(
          `Failed to merge ${data?.label || "items"}. Please try again.`
        );
      } else {
        toast.success(`${data?.label || "Items"} merged successfully`);
        // Invalidate all related queries to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
        queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
        queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
        if (data?.label === "Persona") personaQuery.refetch();
        if (data?.label === "Outcome") outcomeQuery.refetch();
        if (data?.label === "Scenario") scenarioQuery.refetch();
        if (data?.label === "Step") stepQuery.refetch();
        if (data?.label === "Action") actionQuery.refetch();
        if (data?.label === "Api") apiQuery.refetch();
      }
    }
  );

  const { mutate: clonePersona } = useClonePersona((_, err) => {
    toast.dismiss(); // Dismiss the loading toast
    if (err) {
      toast.error(`Failed to clone persona. Please try again.`);
    } else {
      toast.success(`Persona cloned successfully`);
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      personaQuery.refetch();
    }
  });

  const { mutate: cloneScenario } = useCloneScenario((_, err) => {
    toast.dismiss(); // Dismiss the loading toast
    if (err) {
      toast.error(`Failed to clone scenario. Please try again.`);
    } else {
      toast.success(`Scenario cloned successfully`);
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      scenarioQuery.refetch();
    }
  });

  const { mutate: cloneStep } = useCloneStep((_, err) => {
    toast.dismiss(); // Dismiss the loading toast
    if (err) {
      toast.error(`Failed to clone step. Please try again.`);
    } else {
      toast.success(`Step cloned successfully`);
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      stepQuery.refetch();
    }
  });

  const { mutate: cloneAction } = useCloneAction((_, err) => {
    toast.dismiss(); // Dismiss the loading toast
    if (err) {
      toast.error(`Failed to clone action. Please try again.`);
    } else {
      toast.success(`Action cloned successfully`);
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      actionQuery.refetch();
    }
  });

  const { mutate: cloneApi } = useCloneApi((_, err) => {
    toast.dismiss(); // Dismiss the loading toast
    if (err) {
      toast.error(`Failed to clone API endpoint. Please try again.`);
    } else {
      toast.success(`API endpoint cloned successfully`);
      queryClient.invalidateQueries({ queryKey: ["functional-graph"] });
      queryClient.invalidateQueries({ queryKey: ["functional-model-by-id", projectUuid] });
      queryClient.invalidateQueries({ queryKey: ["node-counts", projectUuid] });
      apiQuery.refetch();
    }
  });

  const { mutate: generateEmbeddings } = useGenerateEmbeddings(
    (data) => {
      toast.success(data.message || "Embedding generation started successfully");
    },
    (error) => {
      toast.error(error.message || "Failed to start embedding generation. Please try again.");
    }
  );

  // Dialog states
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [currentNode, setCurrentNode] = useState<any>(null);
  const [currentLabel, setCurrentLabel] = useState("");
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<any>(null);
  const [isEmbeddingConfirmOpen, setEmbeddingConfirmOpen] = useState(false);

  // Clone functionality state
  const [isCloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [personaToClone, setPersonaToClone] = useState<any>(null);
  const [isCloneScenarioDialogOpen, setCloneScenarioDialogOpen] =
    useState(false);
  const [scenarioToClone, setScenarioToClone] = useState<any>(null);
  const [isCloneStepDialogOpen, setCloneStepDialogOpen] = useState(false);
  const [stepToClone, setStepToClone] = useState<any>(null);
  const [isCloneActionDialogOpen, setCloneActionDialogOpen] = useState(false);
  const [actionToClone, setActionToClone] = useState<any>(null);
  const [isCloneApiDialogOpen, setCloneApiDialogOpen] = useState(false);
  const [apiToClone, setApiToClone] = useState<any>(null);

  // The clone button lives in a hover-revealed cluster and is opened via a state
  // boolean rather than <DialogTrigger>, so Radix's default focus-return lands
  // on <body>. Capture the trigger element when opening any clone dialog and
  // restore focus to it in onCloseAutoFocus (WCAG 2.1 §2.4.3).
  const cloneTriggerRef = useRef<HTMLElement | null>(null);
  const handleCloneDialogCloseAutoFocus = (e: Event) => {
    const el = cloneTriggerRef.current;
    if (el && typeof el.focus === "function") {
      e.preventDefault();
      el.focus();
    }
  };

  // Merge functionality state
  const [mergeMode, setMergeMode] = useState<{ [key: string]: boolean }>({});
  const [selectedItemsForMerge, setSelectedItemsForMerge] = useState<{
    [key: string]: any[];
  }>({});
  const [isMergeConfirmOpen, setMergeConfirmOpen] = useState(false);
  const [pendingMerge, setPendingMerge] = useState<{
    label: string;
    items: any[];
    target: any;
  } | null>(null);

  const handleOpenDialog = (label: string, node: any = null) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setCurrentLabel(label);
    setCurrentNode(node);
    setDialogOpen(true);
  };

  // View-only entry point used by the Eye icon on each card for the Viewer
  // role — opens the same FunctionalNodeDialog but skips the edit-block toast.
  // The dialog itself becomes read-only via `isReadOnly={isViewer}`.
  const handleOpenViewDialog = (label: string, node: any) => {
    setCurrentLabel(label);
    setCurrentNode(node);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentNode(null);
    setCurrentLabel("");
  };

  const handleSubmit = (data: any) => {
    if (currentNode) {
      updateNode({
        id: currentNode.id,
        uuid: projectUuid,
        body: { label: currentLabel, data },
      });
    } else {
      let payload = { label: currentLabel, data };
      if (currentLabel === "Outcome") {
        payload.data.personaId = selectedPersona?.id;
      } else if (currentLabel === "Scenario") {
        payload.data.outcomeId = selectedOutcome?.id;
      } else if (currentLabel === "Step") {
        payload.data.scenarioId = selectedScenario?.id;
      } else if (currentLabel === "Action") {
        payload.data.stepId = selectedStep?.id;
      } else if (currentLabel === "Api") {
        payload.data.actionId = selectedAction?.id;
      }
      createNode({ uuid: projectUuid, body: payload });
    }
    handleCloseDialog();
  };

  // NOTE: fetchChildCount removed — backend now cascade-deletes all descendants
  // const fetchChildCount = async (
  //   childLabel: "Outcome" | "Scenario",
  //   parentIdKey: "personaId" | "outcomeId",
  //   parentId: string
  // ): Promise<number | null> => {
  //   try {
  //     const headers = getAuthHeaders();
  //     const url = `${config.isometricApiUrl}/functional-graph/${projectUuid}/${childLabel}?page=1&limit=1&filters[${parentIdKey}][$eq]=${parentId}`;
  //     const res = await apiFetch(url, { headers });
  //     if (!res.ok) return null;
  //     const json = await res.json();
  //     return typeof json?.total === "number" ? json.total : (json?.data?.length ?? 0);
  //   } catch {
  //     return null;
  //   }
  // };

  const handleDelete = async (node: any, label: string) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    // NOTE: Preemptive hierarchy validation removed — backend now cascade-deletes all descendants
    // if (label === "Persona") {
    //   const count = await fetchChildCount("Outcome", "personaId", node.id);
    //   if (count && count > 0) {
    //     toast.error(
    //       "Cannot delete this Persona because it still has Outcomes. Delete its Outcomes first."
    //     );
    //     return;
    //   }
    // } else if (label === "Outcome") {
    //   const count = await fetchChildCount("Scenario", "outcomeId", node.id);
    //   if (count && count > 0) {
    //     toast.error(
    //       "Cannot delete this Outcome because it still has Scenarios. Delete its Scenarios first."
    //     );
    //     return;
    //   }
    // }
    setNodeToDelete({ ...node, label });
    setConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (nodeToDelete) {
      deleteNode({
        id: nodeToDelete.id,
        uuid: projectUuid,
        label: nodeToDelete.label,
      });
      setConfirmOpen(false);
      setNodeToDelete(null);
    }
  };

  // Clone functionality handlers
  const handleClone = (persona: any) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    cloneTriggerRef.current = document.activeElement as HTMLElement | null;
    setPersonaToClone(persona);
    setCloneDialogOpen(true);
  };

  const handleConfirmClone = (newPersonaName: string) => {
    if (personaToClone && projectUuid) {
      toast.loading("Cloning persona...");
      clonePersona({
        projectUuid,
        selectedPersonaId: personaToClone.id,
        body: { newPersonaName },
      });
    }
  };

  const handleCloneScenario = (scenario: any) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    cloneTriggerRef.current = document.activeElement as HTMLElement | null;
    setScenarioToClone(scenario);
    setCloneScenarioDialogOpen(true);
  };

  const handleConfirmCloneScenario = (newScenarioName: string) => {
    if (scenarioToClone && projectUuid) {
      toast.loading("Cloning scenario...");
      cloneScenario({
        projectUuid,
        selectedScenarioId: scenarioToClone.id,
        label: "Scenario",
        body: { newName: newScenarioName },
      });
    }
  };

  // Step clone functionality handlers
  const handleCloneStep = (step: any) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    cloneTriggerRef.current = document.activeElement as HTMLElement | null;
    setStepToClone(step);
    setCloneStepDialogOpen(true);
  };

  const handleConfirmCloneStep = (newStepName: string) => {
    if (stepToClone && projectUuid) {
      toast.loading("Cloning step...");
      cloneStep({
        projectUuid,
        selectedStepId: stepToClone.id,
        label: "Step",
        body: { newName: newStepName },
      });
    }
  };

  // Action clone functionality handlers
  const handleCloneAction = (action: any) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    cloneTriggerRef.current = document.activeElement as HTMLElement | null;
    setActionToClone(action);
    setCloneActionDialogOpen(true);
  };

  const handleConfirmCloneAction = (newActionName: string) => {
    if (actionToClone && projectUuid) {
      toast.loading("Cloning action...");
      cloneAction({
        projectUuid,
        selectedActionId: actionToClone.id,
        label: "Action",
        body: { newName: newActionName },
      });
    }
  };

  // API clone functionality handlers
  const handleCloneApi = (api: any) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    cloneTriggerRef.current = document.activeElement as HTMLElement | null;
    setApiToClone(api);
    setCloneApiDialogOpen(true);
  };

  const handleConfirmCloneApi = (newApiName: string) => {
    if (apiToClone && projectUuid) {
      toast.loading("Cloning API endpoint...");
      cloneApi({
        projectUuid,
        selectedApiId: apiToClone.id,
        label: "Api",
        body: { newName: newApiName },
      });
    }
  };

  // Merge functionality handlers
  const toggleMergeMode = (label: string) => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    setMergeMode((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
    if (!mergeMode[label]) {
      setSelectedItemsForMerge((prev) => ({
        ...prev,
        [label]: [],
      }));
    } else {
      setSelectedItemsForMerge((prev) => {
        const newState = { ...prev };
        delete newState[label];
        return newState;
      });
    }
  };

  const toggleItemSelection = (item: any, label: string) => {
    setSelectedItemsForMerge((prev) => {
      const currentSelection = prev[label] || [];
      const isSelected = currentSelection.some(
        (selected) => selected.id === item.id
      );

      if (isSelected) {
        return {
          ...prev,
          [label]: currentSelection.filter(
            (selected) => selected.id !== item.id
          ),
        };
      } else {
        return {
          ...prev,
          [label]: [...currentSelection, item],
        };
      }
    });
  };

  const handleMergeNodes = (label: string) => {
    const selectedItems = selectedItemsForMerge[label] || [];
    if (selectedItems.length < 2) {
      toast.error("Please select at least 2 items to merge");
      return;
    }

    // Last selected item becomes the target
    const targetItem = selectedItems[selectedItems.length - 1];
    const sourceItems = selectedItems.slice(0, -1);

    setPendingMerge({ label, items: sourceItems, target: targetItem });
    setMergeConfirmOpen(true);
  };

  const executeMerge = () => {
    if (!pendingMerge) return;

    const { label, items, target } = pendingMerge;
    const targetId = target.id;
    const mergeIds = items.map((item) => item.id);

    // Track all involved node IDs (sources + target) to show in-progress state
    setMergingNodeIds(new Set([...mergeIds, targetId]));

    mergeNodes({
      uuid: projectUuid,
      body: {
        label,
        mergeIds,
        targetId,
      },
    });

    // Reset merge state
    setMergeMode((prev) => ({ ...prev, [label]: false }));
    setSelectedItemsForMerge((prev) => {
      const newState = { ...prev };
      delete newState[label];
      return newState;
    });
    setPendingMerge(null);
  };

  const isItemSelected = (item: any, label: string) => {
    const selectedItems = selectedItemsForMerge[label] || [];
    return selectedItems.some((selected) => selected.id === item.id);
  };

  const getSelectedCount = (label: string) => {
    return selectedItemsForMerge[label]?.length || 0;
  };

  const { getAuthHeaders } = useAuthHeaders();

  // Fetch node citations for each selected item using citationRefs
  const { data: personaCitations } = useGetNodeCitations(selectedPersona?.citationRefs??[], projectUuid);
  const { data: outcomeCitations } = useGetNodeCitations(selectedOutcome?.citationRefs??[], projectUuid);
  const { data: scenarioCitations } = useGetNodeCitations(selectedScenario?.citationRefs??[], projectUuid);
  const { data: stepCitations } = useGetNodeCitations(selectedStep?.citationRefs??[], projectUuid);
  const { data: actionCitations } = useGetNodeCitations(selectedAction?.citationRefs??[], projectUuid);

  const handleNodeCitationClick = async (citation: NodeCitation) => {
    const citationType = citation.type;

    // code and prompt types are display-only
    if (citationType === 'code' || citationType === 'prompt' || citationType === 'user') return;

    // jira, figma, confluence are clickable links
    if (citationType === 'jira' || citationType === 'figma' || citationType === 'confluence') {
      window.open(citation.reference, "_blank", "noopener,noreferrer");
      return;
    }

    // document type - fetch signed URL
    const docId = citation.reference;
    if (!docId) return;
    try {
      const signedUrl = await queryClient.fetchQuery({
        queryKey: ["getSignedDocUrl", docId],
        queryFn: () => {
          const headers = getAuthHeaders();
          return getDocumentsSignedUrlById(docId, headers);
        },
        staleTime: 300000,
      });
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleRegenerateEmbeddings = () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    if (!projectUuid) {
      toast.error("Project UUID not found");
      return;
    }
    setEmbeddingConfirmOpen(true);
  };

  const handleConfirmRegenerateEmbeddings = () => {
    if (projectUuid) {
      generateEmbeddings({ projectUuid });
    }
    setEmbeddingConfirmOpen(false);
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const result = await refetchTree();
      const tree = result.data?.tree || [];

      // Transform tree nodes into a clean nested export shape
      const mapApis = (node: any) => ({ id: node.id, ...node.data });
      const mapActions = (node: any) => ({
        id: node.id,
        ...node.data,
        apis: (node.children || []).map(mapApis),
      });
      const mapSteps = (node: any) => ({
        id: node.id,
        ...node.data,
        actions: (node.children || []).map(mapActions),
      });
      const mapScenarios = (node: any) => ({
        id: node.id,
        ...node.data,
        steps: (node.children || []).map(mapSteps),
      });
      const mapOutcomes = (node: any) => ({
        id: node.id,
        ...node.data,
        scenarios: (node.children || []).map(mapScenarios),
      });
      const exportData = {
        exportedAt: new Date().toISOString(),
        projectUuid,
        personas: tree.map((personaNode: any) => ({
          id: personaNode.id,
          ...personaNode.data,
          outcomes: (personaNode.children || []).map(mapOutcomes),
        })),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `functional-data-${projectUuid || "export"}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Functional data exported successfully");
    } catch {
      toast.error("Failed to export functional data");
    } finally {
      setIsExporting(false);
    }
  };

  const actionButtons = (
    <>
      <CustomTooltip
        text={
          appliedFilters.length > 0
            ? `${appliedFilters.length} filter${appliedFilters.length === 1 ? "" : "s"} applied`
            : "Filter nodes"
        }
        side="bottom"
      >
        <Button
          variant="outline"
          size="icon"
          onClick={() => setIsFilterPanelOpen((v) => !v)}
          className={`relative h-7 w-7 border-surface-border text-white hover:bg-surface-hover ${
            appliedFilters.length > 0 ? "ring-2 ring-primary/60" : ""
          }`}
        >
          <Filter className="h-3.5 w-3.5" />
          {appliedFilters.length > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-primary text-[10px] leading-[14px] font-semibold text-center"
              style={{ color: "#ffffff" }}
            >
              {appliedFilters.length}
            </span>
          )}
        </Button>
      </CustomTooltip>

      <CustomTooltip text="Refresh All Data" side="bottom">
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            personaQuery.refetch();
            outcomeQuery.refetch();
            scenarioQuery.refetch();
            stepQuery.refetch();
            actionQuery.refetch();
            apiQuery.refetch();
            toast.success("All functional data refreshed");
          }}
          className="h-7 w-7 border-surface-border text-white hover:bg-surface-hover"
        >
          <RotateCw className="h-3.5 w-3.5" />
        </Button>
      </CustomTooltip>

      {isAdmin && !isViewer && (
        <CustomTooltip text="Re-generate Embeddings" side="bottom">
          <Button
            variant="outline"
            size="icon"
            onClick={handleRegenerateEmbeddings}
            className="h-7 w-7 border-surface-border text-white hover:bg-surface-hover hover:border-primary"
          >
            <Sparkles className="h-3.5 w-3.5" />
          </Button>
        </CustomTooltip>
      )}

      <CustomTooltip text="Export Functional Data" side="bottom">
        <Button
          variant="outline"
          size="icon"
          onClick={handleExportData}
          disabled={isExporting}
          className="h-7 w-7 border-surface-border text-white hover:bg-surface-hover"
        >
          {isExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        </Button>
      </CustomTooltip>
    </>
  );

  return (
    <>
      {/* Portal action buttons into the header bar, or render inline */}
      {actionsPortalRef?.current
        ? createPortal(actionButtons, actionsPortalRef.current)
        : (
          <div className="flex items-center justify-end gap-1 px-4 py-2 flex-shrink-0 border-b border-surface-border">
            {actionButtons}
          </div>
        )}

      <div
        className={`flex-col flex overflow-y-auto h-full px-4 py-3 ${CUSTOM_SCROLLBAR}`}
      >

        {/* BREEZEAI-1054: filter chips (always visible when any filter is on)
            and the filter panel (visible while the button is toggled). Chips
            let the user drop individual predicates without reopening the
            panel; both surfaces stay in sync with `appliedFilters`. */}
        {appliedFilters.length > 0 && (
          <div className="mb-4">
            <FunctionalGraphFilterChips
              filters={appliedFilters}
              onRemove={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
            />
          </div>
        )}
        <Dialog open={isFilterPanelOpen} onOpenChange={setIsFilterPanelOpen}>
          {/* DialogContent defaults (max-w-lg, p-6, gap-4, bg, border, shadow)
              are all neutralized so the panel's own card styling (620px wide,
              own header, own dismiss) is preserved verbatim. The panel already
              renders an X, so hideCloseButton avoids a second one. */}
          <DialogContent
            hideCloseButton
            className="w-auto max-w-[640px] p-0 gap-0 bg-transparent border-0 shadow-none"
            onEscapeKeyDown={(e) => e.stopPropagation()}
          >
            <DialogTitle className="sr-only">Filter nodes</DialogTitle>
            <DialogDescription className="sr-only">
              Match on any label attribute
            </DialogDescription>
            <FunctionalGraphFilterPanel
              applied={appliedFilters}
              onApply={handleApplyFilters}
              onClose={() => setIsFilterPanelOpen(false)}
              customPropertyKeys={{
                Persona: extractCustomKeys(personas, "Persona"),
                Outcome: extractCustomKeys(outcomes, "Outcome"),
                Scenario: extractCustomKeys(scenarios, "Scenario"),
                Step: extractCustomKeys(steps, "Step"),
                Action: extractCustomKeys(actions, "Action"),
                Api: extractCustomKeys(apis, "Api"),
              }}
            />
          </DialogContent>
        </Dialog>

        <div className="flex flex-col gap-3">
            {/* Personas Section */}
            <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h6 className="text-lg text-white font-semibold">Persona</h6>
                  <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                    {personas?.length || 0} of {personasTotal || 0}
                  </span>
                </div>
                <div className="flex gap-2">

                  {mergeMode["Persona"] ? (
                    <>
                      <Button
                        onClick={() => handleMergeNodes("Persona")}
                        variant="default"
                        size="sm"
                        disabled={getSelectedCount("Persona") < 2 || mergingNodeIds.size > 0}
                        className="bg-success hover:bg-success/80"
                      >
                        {mergingNodeIds.size > 0 ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />}
                        {mergingNodeIds.size > 0 ? "Merging..." : `Merge (${getSelectedCount("Persona")})`}
                      </Button>
                      <Button
                        onClick={() => toggleMergeMode("Persona")}
                        variant="destructive"
                        size="sm"
                        className="bg-error hover:bg-error/90 border-error hover:border-error/90 font-medium transition-all duration-200"
                      >
                        Exit Mode
                      </Button>
                    </>
                  ) : (
                    !isViewer && (
                      <>
                        {personas && personas.length >= 2 && (
                          <CustomTooltip text="Merge Mode" side="top">
                            <Button
                              onClick={() => toggleMergeMode("Persona")}
                              variant="outline"
                              size="icon"
                              className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary transition-all duration-200 font-medium"
                            >
                              <Merge className="h-4 w-4" />
                            </Button>
                          </CustomTooltip>
                        )}
                        <CustomTooltip text="Add Persona" side="top">
                          <Button
                            onClick={() => handleOpenDialog("Persona")}
                            variant="outline"
                            size="icon"
                            className="bg-primary hover:bg-primary-dark"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </CustomTooltip>
                      </>
                    )
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {personaQuery.isLoading ? (
                  <div className="text-center py-8">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-10 h-10 bg-surface-dark rounded-full flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <div>
                        <p className="text-white font-medium">Loading personas...</p>
                        <p className="text-muted text-sm">Please wait while we fetch your personas</p>
                      </div>
                    </div>
                  </div>
                ) : personas && personas.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                      {personas.map((item, index) => (
                        <div
                          key={item.persona + index}
                          className={`relative group ${mergeMode["Persona"] &&
                              isItemSelected(item, "Persona")
                              ? "ring-2 ring-primary bg-primary/5"
                              : ""
                            } ${mergingNodeIds.has(item.id) ? "opacity-60 pointer-events-none" : ""}`}
                        >
                          {mergingNodeIds.has(item.id) && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/60 rounded">
                              <div className="flex items-center gap-2 text-sm text-primary">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Merging...
                              </div>
                            </div>
                          )}
                          <PersonaCard
                            title={item.persona}
                            onClick={() =>
                              mergeMode["Persona"]
                                ? toggleItemSelection(item, "Persona")
                                : handlePersonaClick(item)
                            }
                            isActive={
                              mergeMode["Persona"]
                                ? isItemSelected(item, "Persona")
                                : selectedPersona?.id === item.id
                            }
                            headerSize={"text-lg"}
                            contentSize={"text-base"}
                            icon={<PersonaIcon />}
                          />
                          {!mergeMode["Persona"] && !isViewer && (
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <CustomTooltip text="Clone">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleClone(item)}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </CustomTooltip>
                              <CustomTooltip text="Edit">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() =>
                                    handleOpenDialog("Persona", item)
                                  }
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </CustomTooltip>
                              {/* NOTE: Delete button no longer blocked by children — backend cascade-deletes all descendants */}
                              <CustomTooltip text="Delete">
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  onClick={() =>
                                    handleDelete(item, "Persona")
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </CustomTooltip>
                            </div>
                          )}
                          {isViewer && (
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <CustomTooltip text="View Persona">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() =>
                                    handleOpenViewDialog("Persona", item)
                                  }
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </CustomTooltip>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <Pagination
                      currentItems={personas.length}
                      totalItems={personasTotal}
                      currentPage={personaPage}
                      totalPages={personasTotalPages}
                      isLoading={personaQuery.isLoading}
                      onPrevious={handlePersonaPrevPage}
                      onNext={handlePersonaNextPage}
                      canGoPrevious={personaPage > 1}
                      canGoNext={personaPage < personasTotalPages}
                      itemName="personas"
                      showPageNumbers={true}
                      mode="pages"
                    />
                  </>
                ) : (
                  <div className="text-center">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-10 h-10 bg-surface-dark rounded-full flex items-center justify-center">
                        <PersonaIcon className="w-5 h-5 text-muted" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          No personas yet
                        </p>
                        <p className="text-muted text-sm">
                          Create your first persona to get started
                        </p>
                      </div>
                      {!isViewer && (
                        <CustomTooltip text="Create Persona" side="top">
                          <Button
                            onClick={() => handleOpenDialog("Persona")}
                            variant="outline"
                            className="bg-primary hover:bg-primary-dark border-primary text-white"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Persona
                          </Button>
                        </CustomTooltip>
                      )}
                    </div>
                  </div>
                )}
              </div>

            </div>

            {/* Citations for selected Persona */}
            {selectedPersona && personaCitations && personaCitations.length > 0 && (
              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 uppercase tracking-wider">
                    Persona
                  </span>
                  <span className="text-sm text-white truncate">{selectedPersona.persona}</span>
                  <span className="text-xs text-muted">— Citations</span>
                </div>
                <CitationList
                  citations={personaCitations}
                  onCitationClick={handleNodeCitationClick}
                />
              </div>
            )}

            {/* Tasks Section */}
            {selectedPersona && (
              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg text-white font-semibold">Tasks</h4>
                    <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                      {`${outcomes.length} of ${outcomesTotal}`}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {mergeMode["Outcome"] ? (
                      <>
                        <Button
                          onClick={() => handleMergeNodes("Outcome")}
                          variant="default"
                          size="sm"
                          disabled={getSelectedCount("Outcome") < 2 || mergingNodeIds.size > 0}
                          className="bg-success hover:bg-success/80"
                        >
                          {mergingNodeIds.size > 0 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Merge className="h-4 w-4 mr-2" />}
                          {mergingNodeIds.size > 0 ? "Merging..." : `Merge (${getSelectedCount("Outcome")})`}
                        </Button>
                        <Button
                          onClick={() => toggleMergeMode("Outcome")}
                          variant="destructive"
                          size="sm"
                          className="bg-error hover:bg-error/90 border-error hover:border-error/90 font-medium transition-all duration-200"
                        >
                          Exit Mode
                        </Button>
                      </>
                    ) : (
                      !isViewer && (
                        <>
                          <CustomTooltip text="Merge Mode" side="top">
                            <Button
                              onClick={() => toggleMergeMode("Outcome")}
                              variant="outline"
                              size="icon"
                              className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary transition-all duration-200 font-medium"
                            >
                              <Merge className="h-4 w-4" />
                            </Button>
                          </CustomTooltip>
                          <CustomTooltip text="Add Task" side="top">
                            <Button
                              onClick={() => handleOpenDialog("Outcome")}
                              variant="outline"
                              size="icon"
                              className="bg-primary hover:bg-primary-dark"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </CustomTooltip>
                        </>
                      )
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={outcomeSearch}
                    onChange={(e) => setOutcomeSearch(e.target.value)}
                    className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {isLoadingOutcomes ? (
                    <div className="text-center py-3 text-muted">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                        Loading outcomes...
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-2">
                        {outcomes.map((outcome) => (
                          <div
                            key={outcome.id}
                            className={`relative group flex items-center justify-between ${mergeMode["Outcome"] &&
                                isItemSelected(outcome, "Outcome")
                                ? "ring-2 ring-primary bg-primary/5"
                                : ""
                              } ${mergingNodeIds.has(outcome.id) ? "opacity-60 pointer-events-none" : ""}`}
                          >
                            {mergingNodeIds.has(outcome.id) && (
                              <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/60 rounded">
                                <div className="flex items-center gap-2 text-sm text-primary">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Merging...
                                </div>
                              </div>
                            )}
                            <ContentButton
                              content={`${outcome.outcome}`}
                              onClick={() =>
                                mergeMode["Outcome"]
                                  ? toggleItemSelection(outcome, "Outcome")
                                  : handleOutcomeClick(outcome)
                              }
                              isActive={
                                mergeMode["Outcome"]
                                  ? isItemSelected(outcome, "Outcome")
                                  : selectedOutcome?.id === outcome.id
                              }
                              contentSize={"text-base"}
                            />
                            {!mergeMode["Outcome"] && !isViewer && (
                              <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <CustomTooltip text="Edit">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() =>
                                      handleOpenDialog("Outcome", outcome)
                                    }
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                                {/* NOTE: Delete button no longer blocked by children — backend cascade-deletes all descendants */}
                                <CustomTooltip text="Delete">
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() =>
                                      handleDelete(outcome, "Outcome")
                                    }
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                              </div>
                            )}
                            {isViewer && (
                              <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <CustomTooltip text="View Outcome">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() =>
                                      handleOpenViewDialog("Outcome", outcome)
                                    }
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <Pagination
                          currentItems={outcomes.length}
                          totalItems={outcomesTotal}
                          currentPage={outcomePage}
                          totalPages={outcomesTotalPages}
                          isLoading={outcomeQuery.isLoading}
                          onPrevious={handleOutcomePrevPage}
                          onNext={handleOutcomeNextPage}
                          canGoPrevious={outcomePage > 1}
                          canGoNext={outcomePage < outcomesTotalPages}
                          itemName="tasks"
                          showPageNumbers={true}
                          mode="pages"
                        />
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Citations for selected Task */}
            {selectedOutcome && outcomeCitations && outcomeCitations.length > 0 && (
              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 uppercase tracking-wider">
                    Task
                  </span>
                  <span className="text-sm text-white truncate">{selectedOutcome.outcome}</span>
                  <span className="text-xs text-muted">— Citations</span>
                </div>
                <CitationList
                  citations={outcomeCitations}
                  onCitationClick={handleNodeCitationClick}
                />
              </div>
            )}

            {/* Scenarios and Citations Section */}
            {selectedOutcome && (
              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                {/* Scenarios */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <h4 className="text-lg text-white font-semibold">
                        Scenarios
                      </h4>
                      <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                        {`${scenarios.length} of ${scenariosTotal}`}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {mergeMode["Scenario"] ? (
                        <>
                          <Button
                            onClick={() => handleMergeNodes("Scenario")}
                            variant="default"
                            size="sm"
                            disabled={getSelectedCount("Scenario") < 2 || mergingNodeIds.size > 0}
                            className="bg-success hover:bg-success/80"
                          >
                            {mergingNodeIds.size > 0 ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Merge className="h-4 w-4 mr-2" />}
                            {mergingNodeIds.size > 0 ? "Merging..." : `Merge (${getSelectedCount("Scenario")})`}
                          </Button>
                          <Button
                            onClick={() => toggleMergeMode("Scenario")}
                            variant="destructive"
                            size="sm"
                            className="bg-error hover:bg-error/90 border-error hover:border-error/90 font-medium transition-all duration-200"
                          >
                            Exit Mode
                          </Button>
                        </>
                      ) : (
                        !isViewer && (
                          <>
                            <CustomTooltip text="Merge Mode" side="top">
                              <Button
                                onClick={() => toggleMergeMode("Scenario")}
                                variant="outline"
                                size="icon"
                                className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary transition-all duration-200 font-medium"
                              >
                                <Merge className="h-4 w-4" />
                              </Button>
                            </CustomTooltip>
                            <CustomTooltip text="Add Scenario" side="top">
                              <Button
                                onClick={() => handleOpenDialog("Scenario")}
                                variant="outline"
                                size="icon"
                                className="bg-primary hover:bg-primary-dark"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </CustomTooltip>
                          </>
                        )
                      )}
                    </div>
                  </div>

                  <div>
                    <input
                      type="text"
                      placeholder="Search scenarios..."
                      value={scenarioSearch}
                      onChange={(e) => setScenarioSearch(e.target.value)}
                      className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {isLoadingScenarios ? (
                      <div className="text-center py-3 text-muted">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                          Loading scenarios...
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-2">
                          {scenarios.map((scenario) => (
                            <div
                              key={scenario.id}
                              className={`relative group flex items-center justify-between ${mergeMode["Scenario"] &&
                                  isItemSelected(scenario, "Scenario")
                                  ? "ring-2 ring-primary bg-primary/5"
                                  : ""
                                } ${mergingNodeIds.has(scenario.id) ? "opacity-60 pointer-events-none" : ""}`}
                            >
                              {mergingNodeIds.has(scenario.id) && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/60 rounded">
                                  <div className="flex items-center gap-2 text-sm text-primary">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Merging...
                                  </div>
                                </div>
                              )}
                              <ContentButton
                                content={`${scenario.scenario}`}
                                onClick={() =>
                                  mergeMode["Scenario"]
                                    ? toggleItemSelection(scenario, "Scenario")
                                    : handleScenarioClick(scenario)
                                }
                                isActive={
                                  mergeMode["Scenario"]
                                    ? isItemSelected(scenario, "Scenario")
                                    : selectedScenario?.id === scenario.id
                                }
                                contentSize={"text-base"}
                              />
                              {/* String compare: older edits saved the flag as "true"/"false" (BREEZEAI-1373) */}
                              {String(scenario.isDesignGenerated) === "true" && (
                                <CustomTooltip text="Design generated" side="top">
                                  <Palette className="h-3 w-3 mr-2 flex-shrink-0 text-emerald-400 transition-opacity group-hover:opacity-0" />
                                </CustomTooltip>
                              )}
                              {!mergeMode["Scenario"] && !isViewer && (
                                <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <CustomTooltip text="Clone">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() =>
                                        handleCloneScenario(scenario)
                                      }
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                  <CustomTooltip text="Edit">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() =>
                                        handleOpenDialog("Scenario", scenario)
                                      }
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                  <CustomTooltip text="Delete">
                                    <Button
                                      variant="destructive"
                                      size="icon"
                                      onClick={() =>
                                        handleDelete(scenario, "Scenario")
                                      }
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                </div>
                              )}
                              {isViewer && (
                                <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <CustomTooltip text="View Scenario">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() =>
                                        handleOpenViewDialog("Scenario", scenario)
                                      }
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </CustomTooltip>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <Pagination
                            currentItems={scenarios.length}
                            totalItems={scenariosTotal}
                            currentPage={scenarioPage}
                            totalPages={scenariosTotalPages}
                            isLoading={scenarioQuery.isLoading}
                            onPrevious={handleScenarioPrevPage}
                            onNext={handleScenarioNextPage}
                            canGoPrevious={scenarioPage > 1}
                            canGoNext={scenarioPage < scenariosTotalPages}
                            itemName="scenarios"
                            showPageNumbers={true}
                            mode="pages"
                          />
                      </>
                    )}
                  </div>
                </div>

              </div>
            )}

            {/* Citations for selected Scenario */}
            {selectedScenario && scenarioCitations && scenarioCitations.length > 0 && (
              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-500/20 text-green-400 uppercase tracking-wider">
                    Scenario
                  </span>
                  <span className="text-sm text-white truncate">{selectedScenario.scenario}</span>
                  <span className="text-xs text-muted">— Citations</span>
                </div>
                <CitationList
                  citations={scenarioCitations}
                  onCitationClick={handleNodeCitationClick}
                />
              </div>
            )}
          </div>

        {/* Design Header with Actions */}
        {selectedScenario && (
          <div className="mt-2">
            <div className="flex flex-col gap-3">
              {/* Steps Section */}
              <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h4 className="text-lg text-white font-semibold">Steps</h4>
                    <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                      {`${steps.length} of ${stepsTotal}`}
                    </span>
                  </div>
                  {!isViewer && (
                    <CustomTooltip text="Add Step">
                      <Button
                        onClick={() => handleOpenDialog("Step")}
                        variant="outline"
                        size="icon"
                        className="bg-primary hover:bg-primary-dark"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </CustomTooltip>
                  )}
                </div>

                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Search steps..."
                    value={stepSearch}
                    onChange={(e) => setStepSearch(e.target.value)}
                    className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {isLoadingSteps ? (
                    <div className="text-center py-3 text-muted">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                        Loading steps...
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-2">
                        {steps.map((step, index) => (
                          <div
                            key={step.id}
                            className="relative group flex items-center justify-between"
                          >
                            <ContentButton
                              content={`${index + 1}. ${step.step}`}
                              onClick={() => handleStepClick(step)}
                              isActive={selectedStep?.id === step.id}
                              contentSize={"text-base"}
                            />
                            {!isViewer && (
                              <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <CustomTooltip text="Clone">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleCloneStep(step)}
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                                <CustomTooltip text="Edit">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleOpenDialog("Step", step)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                                <CustomTooltip text="Delete">
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => handleDelete(step, "Step")}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                              </div>
                            )}
                            {isViewer && (
                              <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <CustomTooltip text="View Step">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => handleOpenViewDialog("Step", step)}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </CustomTooltip>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <Pagination
                          currentItems={steps.length}
                          totalItems={stepsTotal}
                          currentPage={stepPage}
                          totalPages={stepsTotalPages}
                          isLoading={stepQuery.isLoading}
                          onPrevious={handleStepPrevPage}
                          onNext={handleStepNextPage}
                          canGoPrevious={stepPage > 1}
                          canGoNext={stepPage < stepsTotalPages}
                          itemName="steps"
                          showPageNumbers={true}
                          mode="pages"
                        />
                    </>
                  )}
                </div>
              </div>

              {/* Citations for selected Step */}
              {selectedStep && stepCitations && stepCitations.length > 0 && (
                <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 uppercase tracking-wider">
                      Step
                    </span>
                    <span className="text-sm text-white truncate">{selectedStep.step}</span>
                    <span className="text-xs text-muted">— Citations</span>
                  </div>
                  <CitationList
                    citations={stepCitations}
                    onCitationClick={handleNodeCitationClick}
                  />
                </div>
              )}

              {/* Actions Section */}
              {selectedStep && (
                <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <h4 className="text-lg text-white font-semibold">
                          Actions
                        </h4>
                        <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                          {`${actions.length} of ${actionsTotal}`}
                        </span>
                      </div>
                      {!isViewer && (
                        <CustomTooltip text="Add Action">
                          <Button
                            onClick={() => handleOpenDialog("Action")}
                            variant="outline"
                            size="icon"
                            className="bg-primary hover:bg-primary-dark"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </CustomTooltip>
                      )}
                    </div>

                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Search actions..."
                        value={actionSearch}
                        onChange={(e) => setActionSearch(e.target.value)}
                        className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {isLoadingActions ? (
                        <div className="text-center py-3 text-muted">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                            Loading actions...
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 gap-2">
                            {actions.map((action) => (
                              <div
                                key={action.id}
                                className="relative group flex items-center justify-between"
                              >
                                <ContentButton
                                  onClick={() => handleActionClick(action)}
                                  isActive={selectedAction?.id === action.id}
                                  contentSize={"text-base"}
                                  content={action.action}
                                />
                                {!isViewer && (
                                  <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <CustomTooltip text="Clone">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleCloneAction(action)}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                    <CustomTooltip text="Edit">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() =>
                                          handleOpenDialog("Action", action)
                                        }
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                    <CustomTooltip text="Delete">
                                      <Button
                                        variant="destructive"
                                        size="icon"
                                        onClick={() =>
                                          handleDelete(action, "Action")
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                  </div>
                                )}
                                {isViewer && (
                                  <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <CustomTooltip text="View Action">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() =>
                                          handleOpenViewDialog("Action", action)
                                        }
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          <Pagination
                              currentItems={actions.length}
                              totalItems={actionsTotal}
                              currentPage={actionPage}
                              totalPages={actionsTotalPages}
                              isLoading={actionQuery.isLoading}
                              onPrevious={handleActionPrevPage}
                              onNext={handleActionNextPage}
                              canGoPrevious={actionPage > 1}
                              canGoNext={actionPage < actionsTotalPages}
                              itemName="actions"
                              showPageNumbers={true}
                              mode="pages"
                            />
                        </>
                      )}
                    </div>
                  </div>

                </div>
              )}

              {/* Citations for selected Action */}
              {selectedAction && actionCitations && actionCitations.length > 0 && (
                <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-pink-500/20 text-pink-400 uppercase tracking-wider">
                      Action
                    </span>
                    <span className="text-sm text-white truncate">{selectedAction.action}</span>
                    <span className="text-xs text-muted">— Citations</span>
                  </div>
                  <CitationList
                    citations={actionCitations}
                    onCitationClick={handleNodeCitationClick}
                  />
                </div>
              )}

              {/* Api Endpoints Section */}
              {selectedAction && (
                <div className="bg-surface-card rounded-lg p-3 border border-surface-border">
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <h4 className="text-lg text-white font-semibold">
                          Api Endpoints
                        </h4>
                        <span className="text-sm text-muted bg-surface-dark px-1.5 py-0.5 rounded">
                          {apis.length} of {apisTotal}
                        </span>
                      </div>
                      {!isViewer && (
                        <CustomTooltip text="Add Api">
                          <Button
                            onClick={() => handleOpenDialog("Api")}
                            variant="outline"
                            size="icon"
                            className="bg-primary hover:bg-primary-dark"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </CustomTooltip>
                      )}
                    </div>

                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Search by URL..."
                        value={apiSearch}
                        onChange={(e) => setApiSearch(e.target.value)}
                        className="w-full text-text-primary px-2 py-1.5 bg-surface-input border border-surface-border rounded mb-2 placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                      {isLoadingApis ? (
                        <div className="text-center py-3 text-muted">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-muted border-t-transparent rounded-full animate-spin"></div>
                            Loading api endpoints...
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 gap-2">
                            {apis.map((api) => (
                              <div
                                key={api.id}
                                className="relative group flex items-center justify-between"
                              >
                                <div className="flex-1">
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs font-medium px-2 py-1 rounded bg-primary text-white uppercase">
                                      {api.method}
                                    </span>
                                    <ContentDiv
                                      contentSize={"text-base"}
                                      content={api.url}
                                    />
                                  </div>
                                </div>
                                {!isViewer && (
                                  <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <CustomTooltip text="Clone">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => handleCloneApi(api)}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                    <CustomTooltip text="Edit">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() =>
                                          handleOpenDialog("Api", api)
                                        }
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                    <CustomTooltip text="Delete">
                                      <Button
                                        variant="destructive"
                                        size="icon"
                                        onClick={() => handleDelete(api, "Api")}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                  </div>
                                )}
                                {isViewer && (
                                  <div className="absolute right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <CustomTooltip text="View Api">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() =>
                                          handleOpenViewDialog("Api", api)
                                        }
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    </CustomTooltip>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          <Pagination
                            currentItems={apis.length}
                            totalItems={apisTotal}
                            currentPage={apiPage}
                            totalPages={apisTotalPages}
                            isLoading={apiQuery.isLoading}
                            onPrevious={handleApiPrevPage}
                            onNext={handleApiNextPage}
                            canGoPrevious={apiPage > 1}
                            canGoNext={apiPage < apisTotalPages}
                            itemName="api endpoints"
                            showPageNumbers={true}
                            mode="pages"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      <FunctionalNodeDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        node={currentNode}
        label={currentLabel}
        projectUuid={projectUuid}
        isReadOnly={isViewer}
      />
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        description={(() => {
          // Each entity carries its display name on a lowercased-label field
          // (persona.persona, outcome.outcome, ...); Api is the odd one out
          // and reads best as "GET /path".
          const label = nodeToDelete?.label?.toLowerCase();
          let name: string | undefined;
          if (label === "api") {
            const method = nodeToDelete?.method;
            const url = nodeToDelete?.url;
            if (method && url) name = `${method} ${url}`;
          } else if (label) {
            const val = nodeToDelete?.[label];
            if (typeof val === "string" && val.trim()) name = val;
          }
          if (name) {
            return `Are you sure you want to delete "${name}"? This action cannot be undone.`;
          }
          if (label) {
            return `Are you sure you want to delete this ${label}? This action cannot be undone.`;
          }
          return "Are you sure you want to delete this item? This action cannot be undone.";
        })()}
      />
      <ConfirmDialog
        open={isEmbeddingConfirmOpen}
        onOpenChange={() => setEmbeddingConfirmOpen(false)}
        onConfirm={handleConfirmRegenerateEmbeddings}
        title="Confirm Embedding Regeneration"
        description="Re-generating embeddings is an expensive operation that will recalculate all semantic embeddings for this project. This process may take several minutes. Are you sure you want to proceed?"
      />
      <MergeConfirmationDialog
        open={isMergeConfirmOpen}
        onOpenChange={setMergeConfirmOpen}
        onConfirm={executeMerge}
        label={pendingMerge?.label || ""}
        sourceItems={pendingMerge?.items || []}
        targetItem={pendingMerge?.target}
      />
      <ClonePersonaDialog
        isOpen={isCloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        onConfirm={handleConfirmClone}
        originalPersonaName={personaToClone?.persona}
        onCloseAutoFocus={handleCloneDialogCloseAutoFocus}
      />
      <CloneScenarioDialog
        isOpen={isCloneScenarioDialogOpen}
        onClose={() => setCloneScenarioDialogOpen(false)}
        onConfirm={handleConfirmCloneScenario}
        originalScenarioName={scenarioToClone?.scenario}
        onCloseAutoFocus={handleCloneDialogCloseAutoFocus}
      />
      <CloneStepDialog
        isOpen={isCloneStepDialogOpen}
        onClose={() => setCloneStepDialogOpen(false)}
        onConfirm={handleConfirmCloneStep}
        originalStepName={stepToClone?.step}
        onCloseAutoFocus={handleCloneDialogCloseAutoFocus}
      />
      <CloneActionDialog
        isOpen={isCloneActionDialogOpen}
        onClose={() => setCloneActionDialogOpen(false)}
        onConfirm={handleConfirmCloneAction}
        originalActionName={actionToClone?.action}
        onCloseAutoFocus={handleCloneDialogCloseAutoFocus}
      />
      <CloneApiDialog
        isOpen={isCloneApiDialogOpen}
        onClose={() => setCloneApiDialogOpen(false)}
        onConfirm={handleConfirmCloneApi}
        originalApiUrl={apiToClone?.url}
        onCloseAutoFocus={handleCloneDialogCloseAutoFocus}
      />
    </>
  );
}
